import type { LabeledListConfig } from '@/types/labeled-list';
import type { BadgeItem, LabeledListData, FileItem } from '@/types/dashboard';
import { formatterRegistry } from '@/utils/formatters';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { TooltipIcon } from '@/components/common/TooltipIcon';
import { TooltipWrapper } from '@/components/common/TooltipWrapper';
import { t } from '@/i18n';
import './LabeledListWidget.css';

interface LabeledListWidgetProps {
  config: LabeledListConfig;
  data: LabeledListData | undefined;
}

const LONG_TEXT_THRESHOLD = 100;

function isLongTextValue(displayType: string, value: unknown): value is string {
  if ((displayType !== 'text' && displayType !== 'html') || typeof value !== 'string') {
    return false;
  }
  const plain = displayType === 'html'
    ? value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    : value;
  return plain.length > LONG_TEXT_THRESHOLD;
}

function withAlpha20(color: string): string {
  return `${color.startsWith('#') ? color : `#${color}`}20`;
}

const extensionToIcon = (ext: string): string => {
  const map: Record<string, string> = {
    pdf: 'las la-file-pdf',
    xls: 'las la-file-excel',
    xlsx: 'las la-file-excel',
    csv: 'las la-file-csv',
    doc: 'las la-file-word',
    docx: 'las la-file-word',
    ppt: 'las la-file-powerpoint',
    pptx: 'las la-file-powerpoint',
    jpg: 'las la-file-image',
    jpeg: 'las la-file-image',
    png: 'las la-file-image',
    gif: 'las la-file-image',
    svg: 'las la-file-image',
    webp: 'las la-file-image',
    zip: 'las la-file-archive',
    rar: 'las la-file-archive',
    '7z': 'las la-file-archive',
    tar: 'las la-file-archive',
    gz: 'las la-file-archive',
    mp3: 'las la-file-audio',
    wav: 'las la-file-audio',
    mp4: 'las la-file-video',
    avi: 'las la-file-video',
    mkv: 'las la-file-video',
    js: 'las la-file-code',
    ts: 'las la-file-code',
    php: 'las la-file-code',
    html: 'las la-file-code',
    css: 'las la-file-code',
    json: 'las la-file-code',
    xml: 'las la-file-code',
  };
  return map[ext.toLowerCase()] || 'las la-file-alt';
};

interface LongTextFieldProps {
  label: string;
  value: string;
  isHtml: boolean;
  icon?: string;
  description?: string;
  editUrl?: string;
}

function LongTextField({ label, value, isHtml, icon, description, editUrl }: LongTextFieldProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const contentRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = contentRef.current;
    setIsClamped(el !== null && el.scrollHeight > el.clientHeight);
  }, [value]);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsExpanded(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isExpanded]);

  return (
    <div className="field-long-text">
      <div className="field-long-text-header d-flex align-items-center mb-1">
        <i className={`${icon || 'las la-circle'} me-1 icon`}></i>
        <span className="field-long-text-label">{label}</span>
        {editUrl && (
          <a href={editUrl} className="ms-2 text-decoration-none" title="Изменить статус">
            <i className="las la-pencil-alt icon-link p-1"></i>
          </a>
        )}
        {description && <TooltipIcon description={description} />}
      </div>
      <div className="field-long-text-body">
        <p className="field-long-text-content" ref={contentRef}>
          {isHtml ? <span dangerouslySetInnerHTML={{ __html: value }} /> : value}
        </p>
        {isClamped && (
          <button type="button" className="btn btn-link field-long-text-more" onClick={() => setIsExpanded(true)}>
            {t('dashboard.readAll')}
          </button>
        )}
      </div>
      {isExpanded && createPortal(
        <div
          className="modal d-block long-text-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setIsExpanded(false)}
        >
          <div className="modal-dialog long-text-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content long-text-modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{label}</h5>
                <button type="button" className="btn-close" aria-label={t('dashboard.close')} onClick={() => setIsExpanded(false)} />
              </div>
              <div className="modal-body long-text-modal-body">
                {isHtml ? <div dangerouslySetInnerHTML={{ __html: value }} /> : value}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export function LabeledListWidget({ config, data }: LabeledListWidgetProps) {
  const fields = config.fields ?? [];
  const mode = config.displayMode ?? 'list';
  if (mode === 'badges') {
    return (
      <div className="labeled-list-widget" data-testid="labeled-list-widget">
        {config.title && <h3 className="labeled-list-title">{config.title}</h3>}

        {fields.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center py-4">
            <div className="text-center text-muted">
              <i className="las la-inbox la-2x mb-2 d-block"></i>
              <p className="mb-0 small">{t('dashboard.noData')}</p>
            </div>
          </div>
        ) : (
          <div className="process-teaser-indicators p-3 d-flex align-items-center bg-light-v1 flex-wrap">
            {fields.map((field, idx) => {
              const fieldData = data?.values?.[field.fieldName];
              const badgeColor = field.badgeColor ?? '#dee2e6';
              const chevron = !field.chevronIcon ? 'la-angle-right' : field.chevronIcon === 'none' ? null : field.chevronIcon;
              const description = fieldData?.tooltip ?? field.customDescription ?? field.description ?? field.label;
              const formatted = fieldData ? formatterRegistry.format('number', fieldData.value, {
                useMillions: field.useMillions,
                millionsFrom: field.millionsFrom,
                decimalPlaces: field.decimalPlaces,
              }) : null;
              return (
                <span key={field.fieldName} className="badge-one">
                  {chevron && idx > 0 && <i className={`icon icon-chevron las la-1x ${chevron}`} />}
                  <span className={`badge px-2 d-flex border-bottom-bold border-radius-md justify-content-between align-items-center text-black bg-white`} style={{ borderBottomColor: badgeColor }}>
                    {!fieldData ? (
                      <span className="h6 ms-1 me-1">—</span>
                    ) : fieldData.displayType === 'file' ? (
                      <>
                        {field.icon && <i className={`${field.icon} icon me-1`} />}
                        <span className="h6 ms-1 me-1 d-flex align-items-center gap-1">
                          {(() => {
                            const items = (fieldData.items as FileItem[]) ?? [];
                            if (items.length === 0) return <span>—</span>;
                            return items.map((item, i) => (
                              <a key={i} href={item.url} download className="text-decoration-none d-flex align-items-center">
                                <i className={`${extensionToIcon(item.extension)} me-1`}></i>
                                {item.filename}
                              </a>
                            ));
                          })()}
                        </span>
                      </>
                    ) : fieldData.displayType === 'html' ? (
                      <>
                        {field.icon && <i className={`${field.icon} icon me-1`} />}
                        <span className="h6 ms-1 me-1" dangerouslySetInnerHTML={{ __html: String(fieldData.value ?? '') }} />
                      </>
                    ) : (
                      <>
                        {field.icon && <i className={`${field.icon} icon me-1`} />}
                        <span className="h6 ms-1 me-1">
                          {formatted === '—' ? '—' : (
                            <TooltipWrapper content={`${field.prefix ?? ''}${formatted}${field.suffix ?? ''}`}>
                              <span className="badge-text-truncate">{field.prefix ?? ''}{formatted}{field.suffix ?? ''}</span>
                            </TooltipWrapper>
                          )}
                        </span>
                        {fieldData.delta !== undefined && fieldData.delta !== null && (() => {
                          const delta = fieldData.delta;
                          const colorUp = field.deltaColorUp || '#28a745';
                          const colorDown = field.deltaColorDown || '#dc3545';
                          const colorNeutral = field.deltaColorNeutral || '#6c757d';
                          let color: string;
                          if (delta > 0) color = colorUp;
                          else if (delta < 0) color = colorDown;
                          else color = colorNeutral;
                          const formattedDelta = formatterRegistry.format('delta', delta, {
                            useMillions: field.useMillionsDelta,
                            millionsFrom: field.millionsFromDelta,
                            decimalPlaces: field.decimalPlacesDelta,
                          });
                          return (
                            <span className="border-radius-md p-1 ms-2" style={{ backgroundColor: withAlpha20(color), color }}>
                              {field.deltaPrefix}{formattedDelta}{field.deltaSuffix}
                            </span>
                          );
                        })()}
                      </>
                    )}
                  </span>
                  {description && <TooltipIcon description={description} />}
                </span>
              );
            })}
          </div>
        )}

        {config.description && (
          <div className="labeled-list-panel-description mt-3 text-muted small" data-testid="labeled-list-panel-description">
            {config.description}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="labeled-list-widget" data-testid="labeled-list-widget">
      {config.title && <h3 className="labeled-list-title">{config.title}</h3>}

      {fields.length === 0 ? (
        <div className="d-flex align-items-center justify-content-center py-4">
          <div className="text-center text-muted">
            <i className="las la-inbox la-2x mb-2 d-block"></i>
            <p className="mb-0 small">{t('dashboard.noData')}</p>
          </div>
        </div>
      ) : (
        <div className="list-group">
          {fields.map((field) => {
            const fieldData = data?.values?.[field.fieldName];
            const displayType = fieldData?.displayType ?? 'number';
            const label = field.customLabel ?? field.label ?? field.fieldName;
            const description = fieldData?.tooltip ?? field.customDescription ?? field.description;

            const rawValue = fieldData?.value;
            const isLongLayout =
              fieldData?.longText === true ||
              isLongTextValue(displayType, rawValue); // fallback kept for backward compat
            if (isLongLayout) {
              const longTextValue =
                typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
              return (
                <div
                  key={field.fieldName}
                  className={`list-group-item field-long-text-item field-name--${field.fieldName}`}
                  data-field-name={field.fieldName}
                >
                  <LongTextField
                    label={label}
                    value={longTextValue}
                    isHtml={displayType === 'html'}
                    icon={field.icon}
                    description={description}
                    editUrl={fieldData?.editUrl}
                  />
                </div>
              );
            }

            return (
              <div key={field.fieldName} className={`list-group-item d-flex justify-content-between align-items-center field-name--${field.fieldName}`} data-field-name={field.fieldName}>
                <div className="field-label text-nowrap me-3 d-flex align-items-center">
                  <i className={`${field.icon || 'las la-circle'} me-1 icon`}></i>
                  <span>{label}</span>
                  {description && <TooltipIcon description={description} />}
                </div>
                <div className="field-value text-end d-flex align-items-center">
                  {displayType === 'badge' ? (
                    (() => {
                      const badgeItems = (fieldData?.items ?? []) as BadgeItem[];
                      if (badgeItems.length === 0) {
                        return <span className="text-muted">—</span>;
                      }
                      return (
                        <div className="d-flex flex-wrap gap-1 justify-content-end">
                          {badgeItems.map((item, i) => (
                            <div key={i} className="badge bg-primary text-white py-1 px-2">
                              {item.url ? (
                                <TooltipWrapper content={item.label}>
                                  <a href={item.url} className="text-white text-decoration-none d-inline-flex align-items-center">
                                  {item.avatar && <img src={item.avatar} className="badge-avatar" alt="" />}
                                  <span className="badge-text-truncate">{item.label}</span>
                                </a>
                                </TooltipWrapper>
                              ) : (
                                <>
                                  {item.avatar && <img src={item.avatar} className="badge-avatar" alt="" />}
                                  <span className="badge-text-truncate">{item.label}</span>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()
                  ) : displayType === 'html' ? (
                    <div className="field-value-html" dangerouslySetInnerHTML={{ __html: String(fieldData?.value ?? '') }} />
                  ) : displayType === 'text' ? (
                    <>
                      <span>{String(fieldData?.value ?? '—')}</span>
                      {fieldData?.editUrl && (
                        <a href={fieldData.editUrl} className="ms-2 text-decoration-none" title="Изменить статус">
                          <i className="las la-pencil-alt icon-link p-1"></i>
                        </a>
                      )}
                    </>
                  ) : displayType === 'file' ? (
                    <div className="d-flex flex-column align-items-end gap-1">
                      {(() => {
                        const items = (fieldData?.items as FileItem[]) ?? [];
                        if (items.length === 0) {
                          return <span className="text-muted">—</span>;
                        }
                        return items.map((item, i) => (
                          <div key={i} className="file-item d-flex align-items-center gap-1">
                            <a
                              href={item.url}
                              download
                              className="text-decoration-none d-flex align-items-center gap-1 file-download-link"
                              title={item.description || t('dashboard.fileDownload')}
                            >
                              <i className={`${extensionToIcon(item.extension)} file-icon`}></i>
                              <span className="file-name">{item.filename}</span>
                            </a>
                          </div>
                        ));
                      })()}
                    </div>
                  ) : (
                    (() => {
                      const rawValue = fieldData?.value ?? null;
                      const formatted = formatterRegistry.format('number', rawValue, {
                        useMillions: field.useMillions,
                        millionsFrom: field.millionsFrom,
                        decimalPlaces: field.decimalPlaces,
                      });
                      return (
                        <>
                          <span>{formatted === '—' ? '—' : <>{field.prefix}{formatted}{field.suffix}</>}</span>
                          {fieldData?.delta !== undefined && fieldData?.delta !== null && (() => {
                            const delta = fieldData.delta;
                            const colorUp = field.deltaColorUp || '#28a745';
                            const colorDown = field.deltaColorDown || '#dc3545';
                            const colorNeutral = field.deltaColorNeutral || '#6c757d';
                            let color: string;
                            if (delta > 0) color = colorUp;
                            else if (delta < 0) color = colorDown;
                            else color = colorNeutral;
                            const formattedDelta = formatterRegistry.format('delta', delta, {
                              useMillions: field.useMillionsDelta,
                              millionsFrom: field.millionsFromDelta,
                              decimalPlaces: field.decimalPlacesDelta,
                            });
                            return (
                              <span className="border-radius-md p-1 ms-2" style={{ backgroundColor: withAlpha20(color), color }}>
                                {field.deltaPrefix}{formattedDelta}{field.deltaSuffix}
                              </span>
                            );
                          })()}
                        </>
                      );
                    })()
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {config.description && (
        <div className="labeled-list-panel-description mt-3 text-muted small" data-testid="labeled-list-panel-description">
          {config.description}
        </div>
      )}
    </div>
  );
}
